import type {
  MarkContext,
  MarkOpenRequest,
  MarkPanelSession,
  MarkPanelState,
  MarkSurfaceId,
} from './mark-panel.types';

export type MarkPanelReducerAction =
  | {
      type: 'surfaceRegistered';
      payload: { id: MarkSurfaceId; clientId: string };
    }
  | {
      type: 'surfaceUnregistered';
      payload: { id: MarkSurfaceId; clientId: string };
    }
  | { type: 'opened'; payload: { session: MarkPanelSession } }
  | {
      type: 'newSessionConfirmationRequested';
      payload: { request: MarkOpenRequest };
    }
  | { type: 'newSessionConfirmationDismissed' }
  | { type: 'focusedExisting' }
  | { type: 'closed' }
  | {
      type: 'conversationBound';
      payload: { sessionKey: string; conversationId: string };
    };

export const initialMarkPanelState: MarkPanelState = {
  status: 'closed',
  session: null,
  activeSurface: null,
  pendingOpen: null,
};

const isSameSurface = (
  surface: { id: MarkSurfaceId; clientId: string } | null,
  target: { id: MarkSurfaceId; clientId: string }
): boolean =>
  surface !== null &&
  surface.id === target.id &&
  surface.clientId === target.clientId;

export const markPanelReducer = (
  state: MarkPanelState,
  action: MarkPanelReducerAction
): MarkPanelState => {
  switch (action.type) {
    case 'surfaceRegistered':
      return { ...state, activeSurface: action.payload };

    case 'surfaceUnregistered': {
      const sessionBelongsToSurface =
        state.session !== null &&
        state.session.surfaceId === action.payload.id &&
        state.session.clientId === action.payload.clientId;

      return {
        ...state,
        activeSurface: isSameSurface(state.activeSurface, action.payload)
          ? null
          : state.activeSurface,
        status: sessionBelongsToSurface ? 'closed' : state.status,
        session: sessionBelongsToSurface ? null : state.session,
        pendingOpen: sessionBelongsToSurface ? null : state.pendingOpen,
      };
    }

    case 'opened':
      return {
        ...state,
        status: 'docked',
        session: action.payload.session,
        pendingOpen: null,
      };

    case 'newSessionConfirmationRequested':
      return { ...state, pendingOpen: action.payload.request };

    case 'newSessionConfirmationDismissed':
      return { ...state, pendingOpen: null };

    case 'focusedExisting':
      return { ...state, status: 'docked' };

    case 'closed':
      return { ...state, status: 'closed', session: null, pendingOpen: null };

    case 'conversationBound': {
      if (
        state.session === null ||
        state.session.key !== action.payload.sessionKey
      ) {
        return state;
      }

      return {
        ...state,
        session: {
          ...state.session,
          conversationId: action.payload.conversationId,
        },
      };
    }

    default:
      return state;
  }
};

export type MarkOpenIntent =
  | 'focusExisting'
  | 'confirmNewSession'
  | 'startNewSession';

export const resolveOpenIntent = (
  state: MarkPanelState,
  context: MarkContext
): MarkOpenIntent => {
  if (state.status === 'closed' || !state.session) return 'startNewSession';

  if (state.session.context.key === context.key) return 'focusExisting';

  return state.session.conversationId === null
    ? 'startNewSession'
    : 'confirmNewSession';
};
